import { setUserId } from "firebase/analytics";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type AuthError,
  type User,
} from "firebase/auth";
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from "firebase/firestore";
import { getToken } from "firebase/messaging";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { analytics, auth, db, getMessagingInstance } from "../firebase";

type UserType = "admin" | "manager" | "customer" | "worker" | "agency" | "support";

interface UserProfile {
  uid: string;
  email: string;
  name: string;
  type: UserType;
  isSuperAdmin?: boolean;
  accessRevoked?: boolean;
  phoneNumber?: string;
  profileImageUrl?: string;
  isDeleted?: boolean;
}

export interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setupProfileListener = (uid: string): Unsubscribe => {
    setProfileLoading(true);
    return onSnapshot(
      doc(db, "Users", uid),
      (userDoc) => {
        if (userDoc.exists()) {
          const data = { uid: userDoc.id, ...userDoc.data() } as UserProfile;
          setUserProfile(data);
        } else {
          setUserProfile(null);
        }
        setProfileLoading(false);
      },
      (error) => {
        console.error("Error loading user profile:", error);
        setUserProfile(null);
        setProfileLoading(false);
      },
    );
  };

  useEffect(() => {
    let profileUnsubscribe: Unsubscribe | null = null;

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      // Clean up previous profile listener
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }

      if (user) {
        profileUnsubscribe = setupProfileListener(user.uid);
        updateFcmToken();
      } else {
        setUserProfile(null);
        setProfileLoading(false);
      }
      setLoading(false);

      // analytics id attachment
      setUserId(analytics, user?.uid || null);
    });

    return () => {
      authUnsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, []);

  const updateFcmToken = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;

    const messaging = await getMessagingInstance();
    if (!messaging) return;
    getToken(messaging, {
      vapidKey:
        "BJDNehiMgxmB8RbcXGoFVsM8Phv6KegvU2cVJaqwgSqGInI6Xyw5gfoKJKq4w5RUlwJkCDFi2YLUGDGtPoTg3CE",
      serviceWorkerRegistration: await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      ),
    }).then((token) => {
      if (token) {
        setDoc(doc(db, "UserTokens", user.uid), {
          fcmToken: token,
        });
      }
    });
  };

  const login = async (email: string, password: string): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const userResult = await signInWithEmailAndPassword(auth, email, password);
      console.log("Login successful");
      updateDoc(doc(db, "Users", userResult.user.uid), {
        lastLogin: serverTimestamp(),
      });
      // Profile will be loaded reactively via onAuthStateChanged -> setupProfileListener
    } catch (error) {
      const authError = error as AuthError;
      setError(authError.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      const authError = error as AuthError;
      setError(authError.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    login,
    logout,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
